// Base `env` scaffold generated from the codegen's IMPORTS contract.
//
// Codegen declares every entry of IMPORTS unconditionally, so a host
// that misses even one name dies at instantiate time with a LinkError —
// the exact bug class this guards against when new builtins land.
// makeBaseEnv closes that hole structurally: it walks IMPORTS and
// guarantees a binding for every name, so a host built as
//
//   { ...makeBaseEnv(h, { label }), ...realBindings }
//
// can never fail to link, no matter what imports the compiler grows.
//
// Two tiers of binding:
//
//   1. Universal implementations — builtins every embedding implements
//      identically against the shared heap helpers (allocation
//      primitives, pack/unpack, string helpers, print buffering, slot).
//      These are real and hosts normally keep them as-is.
//
//   2. Warn-once stubs for everything else. Each stub logs once per
//      env instance and returns the safe value recorded in
//      STUB_RETURN_KINDS. Pointer-returning stubs allocate REAL heap
//      objects (empty string / empty array headers) — returning a bare
//      0 would make the script trap the moment it calls len() on the
//      result, which is strictly worse than a visibly-empty value.
import { IMPORTS } from "../codegen/import-contract.js";
import { BUILTIN_FUNCTIONS } from "../types/builtins.js";
import { allocArray, allocString, allocStruct, concatStrings, equalStrings, intToString, uintToString, copyArray, concatArrays, encodeBytes, initArrayField, packBits, readBytesPtr, readStringCell, readStringPtr, reserveString, writeStringCell, subString, truncateString, unpackBits, } from "./memory-utils.js";
import { promoteEscaped, retain, release, reassignEscapedString } from "./escaped-refcount.js";
import { decodeBase64 } from "../util.js";
import { compressBytes, decompressBytes } from "./codec.js";
// Stub return kind for every import makeBaseEnv does NOT implement for
// real. DERIVED from the builtin registry's semantic return types
// (types/builtins.ts): void → "none", string → "emptyString", arrays →
// "emptyArray", everything else (ints, bools, image/client handles) →
// "zero". A future builtin gets its stub automatically; imports with
// no registry entry (the codegen's runtime primitives) fall back to a
// signature-derived kind in makeBaseEnv (has results → "zero", else
// "none") — though all of those are implemented for real below.
function stubKindForReturnType(t) {
    switch (t.kind) {
        case "void": return "none";
        case "string": return "emptyString";
        case "array": return "emptyArray";
        default: return "zero";
    }
}
export const STUB_RETURN_KINDS = Object.freeze(Object.fromEntries(BUILTIN_FUNCTIONS
    .filter(fn => fn.importName !== null)
    .map(fn => [fn.importName ?? fn.name, stubKindForReturnType(fn.returnType)])));
// Names makeBaseEnv implements for real (tier 1). Kept as a set so the
// stub loop can skip them; the implementations are assigned below.
const UNIVERSAL = new Set([
    "print_i32", "print_u32", "print_str", "print_end",
    "pack", "unpack",
    "compress", "decompress",
    "base64_decode",
    "string_from_bytes",
    "substring",
    "slot",
    "alloc_array", "copy_array", "concat_array", "alloc_struct", "init_array_field",
    "concat_strings", "equal_strings", "int_to_string", "uint_to_string",
    "truncate_string", "string_reserve",
    "string_cell_read", "string_cell_write",
    "promote_escaped", "retain", "release", "reassign_escaped_string",
]);
export function makeBaseEnv(h, opts) {
    const label = opts.label;
    const print = opts.print ?? ((line) => {
        if (line.length > 0)
            console.log(`[${label}] ${line}`);
    });
    // Warn once per name per env instance — a script hammering a stubbed
    // import at 60 Hz must not flood the console. Seed it with the silenced
    // names so they never log (they still return their safe stub value).
    const warned = new Set(opts.silence ?? []);
    const makeStub = (name, kind) => (..._args) => {
        if (!warned.has(name)) {
            warned.add(name);
            console.warn(`[${label}] munos import '${name}' has no host binding; using base stub (${kind})`);
        }
        switch (kind) {
            case "zero": return 0;
            case "emptyString": return allocString(h, "");
            case "emptyArray": return allocArray(h, 0, 4);
            case "none": return;
        }
    };
    const env = {};
    for (const spec of IMPORTS) {
        if (UNIVERSAL.has(spec.name))
            continue;
        const kind = STUB_RETURN_KINDS[spec.name]
            ?? (spec.results.length > 0 ? "zero" : "none");
        env[spec.name] = makeStub(spec.name, kind);
    }
    // ----- tier 1: universal implementations -----
    // print(...) is lowered to repeated print_i32/print_str calls plus a
    // closing print_end; one line is buffered per statement.
    let printBuf = "";
    env.print_i32 = (n) => { printBuf += String(n | 0); };
    env.print_u32 = (n) => { printBuf += String(n >>> 0); };
    env.print_str = (ptr) => { printBuf += readStringPtr(h, ptr); };
    env.print_end = () => {
        const line = printBuf;
        printBuf = "";
        print(line);
    };
    // data packing — operates on `length` (not `size`) per spec; the
    // bit-level layout lives with packBits/unpackBits in memory-utils.
    env.pack = (valuesPtr, widthsPtr) => packBits(h, valuesPtr, widthsPtr, (reason) => {
        console.error(`[${label}] pack: ${reason}`);
    });
    env.unpack = (bytesPtr, widthsPtr) => unpackBits(h, bytesPtr, widthsPtr, (reason) => {
        console.error(`[${label}] unpack: ${reason}`);
    });
    // compression — pure u8[] -> u8[] over the single bundled codec (codec.ts),
    // marshalled with the same readBytesPtr/encodeBytes helpers pack uses. One
    // impl here reaches every host, which is the determinism contract
    // (docs/compression.md).
    env.compress = (dataPtr) => encodeBytes(h, compressBytes(readBytesPtr(h, dataPtr)));
    env.decompress = (dataPtr) => encodeBytes(h, decompressBytes(readBytesPtr(h, dataPtr)));
    env.base64_decode = (sPtr) => encodeBytes(h, decodeBase64(readStringPtr(h, sPtr)));
    // `len` is the script-supplied byte count, clamped to the source
    // array's length so a bad count can't read past the data block.
    env.string_from_bytes = (bufPtr, len) => {
        const all = readBytesPtr(h, bufPtr);
        const n = Math.min(len, all.length);
        return encodeBytes(h, all.subarray(0, n));
    };
    // substring(s, start, end) — half-open byte slice [start, end), bounds
    // clamped, empty on end <= start. See subString in memory-utils.
    env.substring = (sPtr, start, end) => subString(h, sPtr, start, end);
    env.slot = (c) => c;
    // runtime allocation primitives — the codegen-emitted heap calls.
    // Sized declarations start filled (the initialization rule), so
    // alloc_array sets length = size = N; literals overwrite the zeros.
    env.alloc_array = (len, elem) => allocArray(h, len, elem);
    env.copy_array = (srcPtr, elem) => copyArray(h, srcPtr, elem);
    env.concat_array = (aPtr, bPtr, elem) => concatArrays(h, aPtr, bPtr, elem);
    env.alloc_struct = (inlineSize) => allocStruct(h, inlineSize);
    env.init_array_field = (addr, length, elem) => initArrayField(h, addr, length, elem);
    env.concat_strings = (aPtr, bPtr) => concatStrings(h, aPtr, bPtr);
    env.equal_strings = (aPtr, bPtr) => equalStrings(h, aPtr, bPtr);
    env.int_to_string = (n) => intToString(h, n);
    env.uint_to_string = (n) => uintToString(h, n);
    env.truncate_string = (sPtr, n) => truncateString(h, sPtr, n);
    env.string_reserve = (srcPtr, capacity) => reserveString(h, srcPtr, capacity);
    env.string_cell_read = (cellAddr) => readStringCell(h, cellAddr);
    env.string_cell_write = (cellAddr, srcPtr, capacity) => writeStringCell(h, cellAddr, srcPtr, capacity);
    // Escaped-generation write-barrier (docs/memory-model.md §"Stage 2"). Real
    // implementations when this member has an escaped heap; otherwise identity /
    // no-ops so a host that doesn't support the partition still links and runs (it
    // simply reclaims nothing — and must not rewind barrier-rewindable events).
    const esc = opts.escapedHeap;
    if (esc !== undefined) {
        env.promote_escaped = (ptr, stride, isHeap) => promoteEscaped(h, esc, ptr, stride, isHeap !== 0);
        env.retain = (ptr) => { retain(h, ptr); };
        env.release = (ptr, stride, isHeap) => {
            release(h, esc, ptr, stride, isHeap !== 0);
        };
        env.reassign_escaped_string = (oldPtr, newPtr) => reassignEscapedString(h, esc, oldPtr, newPtr);
    }
    else {
        env.promote_escaped = (ptr) => ptr;
        env.retain = () => { };
        env.release = () => { };
        // No escaped heap → no reclamation: the store just keeps the new value.
        env.reassign_escaped_string = (_oldPtr, newPtr) => newPtr;
    }
    return env;
}
//# sourceMappingURL=base-env.js.map